import { useRef } from "react";
import { Download, X, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useOrderById } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import type { OrderResponseDto } from "@/types/order.dto";
import { cn } from "@/lib/utils";

interface TaxInvoiceModalProps {
  orderId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TaxInvoiceModal = ({ orderId, open, onOpenChange }: TaxInvoiceModalProps) => {
  const { data: order, isLoading } = useOrderById(orderId ?? 0);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!order) return;
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;

    const printWindow = window.open("", "_blank", "width=900,height=1000");
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tax Invoice - ${order.orderNumber}</title>
          <meta charset="utf-8" />
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              color: #000;
              background: #fff;
              margin: 0;
              padding: 15px;
              font-size: 11px;
              line-height: 1.4;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              margin-bottom: 10px;
              min-width: 0 !important;
            }
            th, td {
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
              white-space: normal;
            }
            th {
              border-top: 1px solid #000;
              border-bottom: 1px solid #000;
              font-weight: 700;
            }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-bold { font-weight: 700; }
            .border-t { border-top: 1px solid #000; }
            .border-b { border-bottom: 1px solid #000; }
            .border-b-2 { border-bottom: 2px solid #000; }
            .border-dashed { border-style: dashed; }
            .border { border: 1px solid #000; }
            .uppercase { text-transform: uppercase; }
            .tracking-wider { letter-spacing: 0.05em; }
            .my-4 { margin-top: 12px; margin-bottom: 12px; }
            .py-2 { padding-top: 6px; padding-bottom: 6px; }
            .invoice-scroll { overflow: visible !important; }
            .invoice-meta { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 20px !important; }
            .invoice-header { display: flex !important; justify-content: space-between !important; flex-direction: row !important; }
            .invoice-mobile-cards { display: none !important; }
            .invoice-desktop-table { display: block !important; }
            .qr-code { width: 70px; height: 70px; }
            .break-all { word-break: break-all; }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return new Date().toLocaleDateString("en-IN");
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const calculateTaxes = (orderData: OrderResponseDto) => {
    const items = orderData.items || [];
    let totalGross = 0;
    let totalDiscount = orderData.discountAmount || 0;
    let totalTaxable = 0;
    let totalTax = orderData.taxAmount || 0;

    const formattedItems = items.map((item) => {
      const gross = item.unitPrice * item.quantity;
      const discount = item.discountAmount || 0;
      const taxable = (gross - discount) / 1.18;
      const tax = gross - discount - taxable;
      totalGross += gross;
      totalTaxable += taxable;
      totalTax += tax;

      return {
        ...item,
        gross,
        discount,
        taxable: Math.max(0, taxable),
        tax: Math.max(0, tax),
        rate: 18.0,
      };
    });

    if (totalTax === 0) {
      totalTax = (orderData.totalAmount * 0.18) / 1.18;
      totalTaxable = orderData.totalAmount - totalTax;
    }

    return {
      formattedItems,
      totalGross,
      totalDiscount,
      totalTaxable,
      totalTax,
    };
  };

  const money = (n: number) => n.toFixed(2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[94vh] w-[calc(100vw-1rem)] max-w-4xl flex-col overflow-hidden rounded-2xl border-border/80 bg-muted/30 p-0 sm:w-full",
          "[&>button]:hidden",
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Tax Invoice</DialogTitle>
        </DialogHeader>

        {/* Top Control Bar */}
        <div className="flex flex-col gap-3 border-b border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-bold leading-none text-foreground">Tax Invoice</h3>
              <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                {order ? `#${order.orderNumber}` : "Loading..."}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              onClick={handlePrint}
              disabled={isLoading || !order}
              className="h-9 flex-1 gap-2 rounded-xl px-3 text-sm font-semibold shadow-sm sm:flex-none sm:px-4"
            >
              <Download className="h-4 w-4" />
              <span className="sm:hidden">Download</span>
              <span className="hidden sm:inline">Download / Print</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-xl text-muted-foreground hover:text-foreground"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {isLoading || !order ? (
            <div className="space-y-4 rounded-xl border border-border bg-card p-6">
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <div
              ref={printRef}
              className="mx-auto max-w-3xl rounded-xl border border-neutral-300 bg-white p-4 font-sans text-xs leading-relaxed text-black shadow-md selection:bg-neutral-200 sm:p-8"
            >
              {/* Header Title */}
              <div className="invoice-header mb-4 flex flex-col gap-4 pb-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1 sm:max-w-[65%]">
                  <h1 className="mb-2 text-lg font-bold uppercase tracking-wide text-black sm:text-xl">
                    Tax Invoice
                  </h1>
                  <p className="text-sm font-bold text-black">
                    Sold By: {order.storeName || "Bihari Mall Internet Private Limited"}
                  </p>
                  <p className="text-[11px] italic leading-snug text-neutral-700">
                    <span className="font-semibold not-italic">Ship-from Address:</span> Buildings Alyssa,
                    Begonia & Clover, Embassy Tech Village, Outer Ring Road, Devarabeesanahalli Village,
                    Bengaluru, Karnataka, IN - 560103, CIN: U51109KA2012PTC066107
                  </p>
                  <p className="mt-1 text-[11px] font-bold text-black">
                    GSTIN - <span className="font-mono">29AACCF0683K1ZD</span>
                  </p>
                </div>

                <div className="flex shrink-0 flex-row items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
                  <svg
                    viewBox="0 0 100 100"
                    className="qr-code h-16 w-16 border border-black bg-white p-1 sm:h-20 sm:w-20"
                  >
                    <rect width="100" height="100" fill="white" />
                    <rect x="5" y="5" width="30" height="30" fill="black" />
                    <rect x="10" y="10" width="20" height="20" fill="white" />
                    <rect x="15" y="15" width="10" height="10" fill="black" />
                    <rect x="65" y="5" width="30" height="30" fill="black" />
                    <rect x="70" y="10" width="20" height="20" fill="white" />
                    <rect x="75" y="15" width="10" height="10" fill="black" />
                    <rect x="5" y="65" width="30" height="30" fill="black" />
                    <rect x="10" y="70" width="20" height="20" fill="white" />
                    <rect x="15" y="75" width="10" height="10" fill="black" />
                    <rect x="42" y="10" width="6" height="6" fill="black" />
                    <rect x="50" y="10" width="6" height="6" fill="black" />
                    <rect x="42" y="20" width="6" height="6" fill="black" />
                    <rect x="42" y="30" width="6" height="6" fill="black" />
                    <rect x="50" y="30" width="6" height="6" fill="black" />
                    <rect x="10" y="42" width="6" height="6" fill="black" />
                    <rect x="20" y="42" width="6" height="6" fill="black" />
                    <rect x="30" y="42" width="6" height="6" fill="black" />
                    <rect x="42" y="42" width="6" height="6" fill="black" />
                    <rect x="58" y="42" width="6" height="6" fill="black" />
                    <rect x="70" y="42" width="6" height="6" fill="black" />
                    <rect x="82" y="42" width="6" height="6" fill="black" />
                    <rect x="42" y="50" width="6" height="6" fill="black" />
                    <rect x="58" y="50" width="6" height="6" fill="black" />
                    <rect x="70" y="50" width="6" height="6" fill="black" />
                    <rect x="42" y="65" width="6" height="6" fill="black" />
                    <rect x="50" y="65" width="6" height="6" fill="black" />
                    <rect x="65" y="65" width="15" height="15" fill="black" />
                    <rect x="70" y="70" width="5" height="5" fill="white" />
                    <rect x="42" y="82" width="6" height="6" fill="black" />
                    <rect x="58" y="82" width="6" height="6" fill="black" />
                    <rect x="70" y="82" width="12" height="6" fill="black" />
                    <rect x="85" y="82" width="6" height="6" fill="black" />
                  </svg>

                  <div className="min-w-0 border border-dashed border-black bg-neutral-50 px-2.5 py-1 sm:px-3 sm:text-right">
                    <p className="text-[11px] font-bold text-black">
                      Invoice Number #{" "}
                      <span className="break-all font-mono">
                        {order.orderNumber.replace("ORD", "FBF")}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="my-3 border-t border-black" />

              {/* Order Info & Billing Address — stack on mobile */}
              <div className="invoice-meta my-3 grid grid-cols-1 gap-4 text-[11px] sm:grid-cols-2 sm:gap-6">
                <div className="min-w-0 space-y-1.5">
                  <p className="text-sm font-bold text-black">
                    Order ID:{" "}
                    <span className="break-all font-mono font-bold">{order.orderNumber}</span>
                  </p>
                  <p className="font-semibold text-black">
                    Order Date: <span className="font-normal">{formatDate(order.createdAt)}</span>
                  </p>
                  <p className="font-semibold text-black">
                    Invoice Date: <span className="font-normal">{formatDate(order.createdAt)}</span>
                  </p>
                </div>

                <div className="min-w-0 space-y-0.5">
                  <p className="mb-1 text-sm font-bold text-black">Billing Address</p>
                  <p className="font-bold text-black">
                    {order.shippingAddress?.fullName || order.customerName}
                  </p>
                  <p className="break-words text-neutral-800">
                    {order.shippingAddress?.addressLine1}
                    {order.shippingAddress?.addressLine2
                      ? `, ${order.shippingAddress.addressLine2}`
                      : ""}
                  </p>
                  <p className="break-words text-neutral-800">
                    {order.shippingAddress?.landmark
                      ? `Near ${order.shippingAddress.landmark}, `
                      : ""}
                    {order.shippingAddress?.city} {order.shippingAddress?.pincode}{" "}
                    {order.shippingAddress?.state || "Bihar"}
                  </p>
                  <p className="mt-1 font-medium text-neutral-800">
                    Phone: {order.shippingAddress?.phone || order.customerPhone || "xxxxxxxxxx"}
                  </p>
                </div>
              </div>

              <div className="my-3 border-t border-black" />

              {(() => {
                const taxes = calculateTaxes(order);
                const totalItemCount = order.items.reduce((acc, item) => acc + item.quantity, 0);
                const deliveryTaxable = order.deliveryCharge ? order.deliveryCharge / 1.18 : 0;
                const deliveryTax = order.deliveryCharge
                  ? order.deliveryCharge - order.deliveryCharge / 1.18
                  : 0;
                const grossTotal = taxes.totalGross + (order.deliveryCharge || 0);
                const taxableTotal = taxes.totalTaxable + deliveryTaxable;
                const taxTotal = taxes.totalTax + deliveryTax;

                const lineRows = [
                  ...taxes.formattedItems.map((item) => ({
                    key: `item-${item.id ?? item.productName}`,
                    name: item.productName,
                    meta: item.variantInfo ? `Variant: ${item.variantInfo}` : null,
                    hsn: "HSN/SAC: 998599 • IGST: 18.0 %",
                    qty: item.quantity,
                    gross: item.gross,
                    discount: item.discount,
                    taxable: item.taxable,
                    tax: item.tax,
                    total: item.totalPrice,
                    muted: false,
                  })),
                  ...(order.deliveryCharge > 0
                    ? [
                        {
                          key: "delivery",
                          name: "Platform & Delivery Charges",
                          meta: null as string | null,
                          hsn: "SAC: 998599 • IGST: 18.0 %",
                          qty: 1,
                          gross: order.deliveryCharge,
                          discount: 0,
                          taxable: deliveryTaxable,
                          tax: deliveryTax,
                          total: order.deliveryCharge,
                          muted: true,
                        },
                      ]
                    : []),
                ];

                return (
                  <div className="my-4">
                    {/* Mobile stacked line items — no overlapping columns */}
                    <div className="invoice-mobile-cards space-y-3 sm:hidden">
                      {lineRows.map((row) => (
                        <div
                          key={row.key}
                          className={cn(
                            "rounded-lg border border-neutral-300 p-3",
                            row.muted && "bg-neutral-50",
                          )}
                        >
                          <p className="text-sm font-bold text-black">{row.name}</p>
                          {row.meta ? (
                            <p className="mt-0.5 text-[10px] text-neutral-600">{row.meta}</p>
                          ) : null}
                          <p className="mt-0.5 font-mono text-[10px] text-neutral-600">{row.hsn}</p>

                          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                            <div>
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                Qty
                              </dt>
                              <dd className="font-medium text-black">{row.qty}</dd>
                            </div>
                            <div className="text-right">
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                Total ₹
                              </dt>
                              <dd className="font-mono font-bold text-black">{money(row.total)}</dd>
                            </div>
                            <div>
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                Gross ₹
                              </dt>
                              <dd className="font-mono text-black">{money(row.gross)}</dd>
                            </div>
                            <div className="text-right">
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                Discount
                              </dt>
                              <dd className="font-mono text-black">{money(row.discount)}</dd>
                            </div>
                            <div>
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                Taxable ₹
                              </dt>
                              <dd className="font-mono text-black">{money(row.taxable)}</dd>
                            </div>
                            <div className="text-right">
                              <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                IGST ₹
                              </dt>
                              <dd className="font-mono text-black">{money(row.tax)}</dd>
                            </div>
                          </dl>
                        </div>
                      ))}

                      <div className="rounded-lg border-2 border-black p-3">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <span className="text-xs font-bold uppercase text-black">Total</span>
                          <span className="text-xs font-bold text-black">Qty {totalItemCount}</span>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
                          <div>
                            <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Gross ₹
                            </dt>
                            <dd className="font-mono font-bold">{money(grossTotal)}</dd>
                          </div>
                          <div className="text-right">
                            <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Discount
                            </dt>
                            <dd className="font-mono font-bold">{money(taxes.totalDiscount)}</dd>
                          </div>
                          <div>
                            <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Taxable ₹
                            </dt>
                            <dd className="font-mono font-bold">{money(taxableTotal)}</dd>
                          </div>
                          <div className="text-right">
                            <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              IGST ₹
                            </dt>
                            <dd className="font-mono font-bold">{money(taxTotal)}</dd>
                          </div>
                        </dl>
                        <div className="mt-3 flex items-baseline justify-between border-t border-black pt-2">
                          <span className="text-sm font-bold text-black">Invoice Total</span>
                          <span className="font-mono text-base font-black text-black">
                            ₹ {money(order.totalAmount)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop / print table — scrollable on narrow screens as fallback */}
                    <div className="invoice-desktop-table invoice-scroll hidden overflow-x-auto sm:block">
                      <table className="w-full min-w-[640px] border-collapse text-left text-[11px]">
                        <thead>
                          <tr className="border-y border-black text-black">
                            <th className="w-[34%] py-2 text-left font-bold">Description</th>
                            <th className="w-[8%] whitespace-nowrap py-2 text-center font-bold">Qty</th>
                            <th className="w-[13%] whitespace-nowrap py-2 text-right font-bold">
                              Gross Amount ₹
                            </th>
                            <th className="w-[12%] whitespace-nowrap py-2 text-right font-bold">
                              Discount
                            </th>
                            <th className="w-[13%] whitespace-nowrap py-2 text-right font-bold">
                              Taxable value ₹
                            </th>
                            <th className="w-[10%] whitespace-nowrap py-2 text-right font-bold">
                              IGST ₹
                            </th>
                            <th className="w-[10%] whitespace-nowrap py-2 text-right font-bold">
                              Total ₹
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200">
                          {lineRows.map((row) => (
                            <tr
                              key={row.key}
                              className={cn("align-top", row.muted && "bg-neutral-50/60")}
                            >
                              <td className="py-2 pr-2">
                                <p className="font-bold text-black">{row.name}</p>
                                {row.meta ? (
                                  <p className="text-[10px] text-neutral-600">{row.meta}</p>
                                ) : null}
                                <p className="mt-0.5 font-mono text-[10px] text-neutral-600">
                                  {row.hsn}
                                </p>
                              </td>
                              <td className="whitespace-nowrap py-2 text-center font-medium">
                                {row.qty}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right font-mono">
                                {money(row.gross)}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right font-mono">
                                {money(row.discount)}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right font-mono">
                                {money(row.taxable)}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right font-mono">
                                {money(row.tax)}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right font-mono font-bold text-black">
                                {money(row.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-y-2 border-black text-xs font-bold text-black">
                            <td className="py-2.5 text-right font-bold uppercase">Total</td>
                            <td className="whitespace-nowrap py-2.5 text-center font-bold">
                              {totalItemCount}
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-right font-mono font-bold">
                              {money(grossTotal)}
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-right font-mono font-bold">
                              {money(taxes.totalDiscount)}
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-right font-mono font-bold">
                              {money(taxableTotal)}
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-right font-mono font-bold">
                              {money(taxTotal)}
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-right font-mono text-sm font-black">
                              {money(order.totalAmount)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Grand Total & Signatory Box */}
              <div className="my-6 flex justify-end items-end pt-2">
                <div className="w-full space-y-3 text-right sm:w-auto">
                  <div className="flex items-baseline justify-between gap-3 sm:justify-end">
                    <span className="text-base font-bold text-black">Grand Total</span>
                    <span className="font-mono text-xl font-black text-black">
                      ₹ {order.totalAmount.toFixed(2)}
                    </span>
                  </div>

                  <div className="pt-2">
                    <p className="text-[11px] font-bold text-black">
                      {order.storeName || "Bihari Mall Internet Private Limited"}
                    </p>

                    <div className="mt-2 inline-block rounded border border-neutral-300 bg-neutral-50 px-4 py-2 text-center">
                      <div className="font-serif text-lg font-bold italic tracking-widest text-neutral-800 selection:bg-none">
                        D. Roychowdhury
                      </div>
                      <div className="mt-1 border-t border-neutral-400 pt-0.5 text-[9px] font-bold uppercase tracking-wider text-neutral-600">
                        Authorized Signatory
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="my-4 border-t border-black" />

              <div className="flex flex-col items-start justify-between gap-3 pt-2 text-[10px] text-neutral-700 sm:flex-row sm:items-center">
                <div className="min-w-0 space-y-0.5">
                  <p className="break-words italic">
                    Regd. office:{" "}
                    <span className="font-semibold not-italic">
                      Bihari Mall Internet Private Limited
                    </span>
                    , Buildings Alyssa, Begonia & Clover, Embassy Tech Village, Outer Ring Road,
                    Bengaluru, Karnataka - 560103 CIN: U51109KA2012PTC066107
                  </p>
                  <p className="font-bold text-black">
                    Contact Bihari Mall: 044-45614700 | 044-67415800 || www.biharimall.com/helpcentre
                  </p>
                </div>

                <div className="flex shrink-0 flex-col items-start sm:items-end">
                  <div className="flex items-center gap-1 text-xs font-bold text-primary">
                    <span>Bihari Mall</span>
                    <span className="rounded bg-primary px-1 text-[10px] font-normal text-white">
                      Thank You!
                    </span>
                  </div>
                  <p className="mt-0.5 font-mono text-[9px] text-neutral-500">Page 1 of 1</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
